import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import EmptyState from 'components/ui/EmptyState';
import { PageHeader } from 'components/ui/page-header';
import Skeleton from 'components/ui/skeleton';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import {
  QuestionReportsInboxView,
  useQuestionReportsInbox,
  useSetQuestionReportStatus,
  type QuestionReportStatus,
} from 'modules/questionReports';

/** The questions learners reported on this test. Authors with the edit permission resolve or dismiss them here. */
export const QuestionReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams();
  const [status, setStatus] = useState<QuestionReportStatus>('OPEN');
  const canTriage = usePermission(PERMISSIONS.TESTS_EDIT);
  const { data, isLoading, isError, refetch } = useQuestionReportsInbox(testId, status);
  const setReportStatus = useSetQuestionReportStatus(testId ?? '');

  const header = <PageHeader title={t('question_reports.page_title')} description={t('question_reports.page_description')} />;

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy>
        {header}
        <Skeleton variant="rounded" className="h-10 w-full" />
        <Skeleton variant="rounded" className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          icon={<AlertCircle />}
          title={t('question_reports.page_error_title')}
          secondaryAction={{ label: t('retry'), onClick: () => void refetch() }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {header}
      <QuestionReportsInboxView
        inbox={data}
        status={status}
        onStatusChange={setStatus}
        canTriage={canTriage}
        questionsHref={`/app/tests/${testId}/questions`}
        pendingQuestionId={setReportStatus.isPending ? setReportStatus.variables?.questionId : undefined}
        onSetStatus={(questionId, next) => setReportStatus.mutate({ questionId, status: next })}
      />
    </div>
  );
};

export default QuestionReportsPage;
